export type BrowserEnvironmentMode = "local-browser" | "hosted-staging" | "hosted-acceptance" | "hosted-production";

/**
 * This ref is deliberately not a credential. It prevents a later disposable
 * acceptance run from being pointed at the pristine staging project by mistake.
 */
export const DISPOSABLE_HOSTED_ACCEPTANCE_PROJECT_REF = import.meta.env.DEV ? "unthqcklatwyqbopdfsk" : "";
export const DISPOSABLE_HOSTED_ACCEPTANCE_PROJECT_NAME = import.meta.env.DEV ? "magnum-opus-client-portal-acceptance" : "";
export const DISPOSABLE_HOSTED_ACCEPTANCE_REGION = import.meta.env.DEV ? "eu-west-2" : "";

export interface BrowserEnvironmentInput {
  target?: string;
  dataMode?: string;
  supabaseUrl?: string;
  supabaseAnonKey?: string;
  expectedProjectRef?: string;
  expectedProjectName?: string;
  expectedRegion?: string;
  variables?: Record<string, string | undefined>;
}

export interface ValidatedBrowserEnvironment {
  mode: BrowserEnvironmentMode;
  dataMode: string;
  supabase?: { url: string; anonKey: string; projectRef: string; projectName: string; region: string };
}

const forbiddenBrowserVariable = /^(?:VITE_.*(?:SERVICE|ROLE|PASSWORD|TOKEN|TOTP|SECRET|PRIVATE).*|VITE_DATABASE_URL)$/iu;
const loopback = (hostname: string) => hostname === "localhost" || hostname === "127.0.0.1";

export function validateBrowserEnvironment(input: BrowserEnvironmentInput): ValidatedBrowserEnvironment {
  for (const [name, value] of Object.entries(input.variables ?? {})) {
    if (typeof value === "string" && value.trim() && forbiddenBrowserVariable.test(name)) throw new Error(`${name} is not browser-safe.`);
  }
  const target = input.target?.trim() || "local-browser";
  const developmentAcceptance = import.meta.env.DEV && target === "hosted-acceptance";
  const production = target === "hosted-production";
  if (target !== "local-browser" && target !== "hosted-staging" && !developmentAcceptance && !production) throw new Error("Browser persistence target is ambiguous.");
  const hasEndpoint = Boolean(input.supabaseUrl?.trim() || input.supabaseAnonKey?.trim());
  if (target === "local-browser") {
    if (hasEndpoint) throw new Error("Local browser mode must not accept a Supabase endpoint.");
    return { mode: "local-browser", dataMode: input.dataMode?.trim() || "synthetic" };
  }
  if (!input.supabaseUrl?.trim() || !input.supabaseAnonKey?.trim()) throw new Error("Hosted Supabase requires a publishable endpoint and key.");
  if (!input.expectedProjectRef?.trim() || !input.expectedProjectName?.trim() || !input.expectedRegion?.trim()) throw new Error("Hosted project identity is incomplete.");
  let url: URL;
  try { url = new URL(input.supabaseUrl); } catch { throw new Error("Hosted staging endpoint must be a valid HTTPS URL."); }
  if (url.protocol !== "https:" || loopback(url.hostname) || url.username || url.password || url.search || url.hash) throw new Error("Hosted staging endpoint must be a credential-free HTTPS non-loopback origin.");
  if (!url.hostname.endsWith(".supabase.co")) throw new Error("Hosted staging endpoint must use a Supabase project origin.");
  const projectRef = url.hostname.split(".")[0];
  if (projectRef !== input.expectedProjectRef.trim()) throw new Error("Hosted staging project reference does not match the endpoint.");
  if (developmentAcceptance && projectRef !== DISPOSABLE_HOSTED_ACCEPTANCE_PROJECT_REF) {
    throw new Error("Hosted acceptance must target the authorised disposable acceptance project.");
  }
  if (developmentAcceptance && input.expectedProjectName.trim() !== DISPOSABLE_HOSTED_ACCEPTANCE_PROJECT_NAME) {
    throw new Error("Hosted acceptance must target the authorised disposable acceptance project name.");
  }
  if (developmentAcceptance && input.expectedRegion.trim() !== DISPOSABLE_HOSTED_ACCEPTANCE_REGION) {
    throw new Error("Hosted acceptance must target the authorised disposable acceptance region.");
  }
  const dataMode = input.dataMode?.trim() || "synthetic";
  if (developmentAcceptance && dataMode !== "synthetic") {
    throw new Error("Hosted acceptance requires synthetic data mode.");
  }
  if (production && dataMode !== "live") throw new Error("Hosted production requires live data mode.");
  if (production && input.expectedRegion.trim() !== "eu-west-2") throw new Error("Hosted production requires the authorised production region.");
  return { mode: target, dataMode, supabase: { url: url.origin, anonKey: input.supabaseAnonKey.trim(), projectRef, projectName: input.expectedProjectName.trim(), region: input.expectedRegion.trim() } };
}
